import React, { useEffect, useMemo, useRef, useState } from "react";
import { SECOND_MS, SHORT_DELAY_MS } from "../../../shared/constants";

function getSiteKey(explicitSiteKey) {
  return explicitSiteKey || process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
}

const TURNSTILE_LOAD_TIMEOUT_MS = 10 * SECOND_MS;
const TURNSTILE_POLL_INTERVAL_MS = SHORT_DELAY_MS;
const TURNSTILE_MOUNT_TIMEOUT_MS = 8 * SECOND_MS;
const TURNSTILE_RETRY_INTERVAL_MS = 8 * SECOND_MS;

function waitForTurnstile({
  timeoutMs = TURNSTILE_LOAD_TIMEOUT_MS,
  intervalMs = TURNSTILE_POLL_INTERVAL_MS,
} = {}) {
  const start = Date.now();
  return new Promise((resolve, reject) => {
    const tick = () => {
      const api = typeof window !== "undefined" ? window.turnstile : null;
      if (api?.render) return resolve(api);
      if (Date.now() - start > timeoutMs) {
        return reject(
          new Error(
            "We encountered an unexpected issue. Please refresh the page and try again.",
          ),
        );
      }
      setTimeout(tick, intervalMs);
    };
    tick();
  });
}

function waitForTurnstileMount(
  containerEl,
  { timeoutMs = TURNSTILE_MOUNT_TIMEOUT_MS } = {},
) {
  return new Promise((resolve) => {
    if (!containerEl) return resolve(false);

    const hasMountedContent = () =>
      Boolean(containerEl.querySelector("iframe")) ||
      Boolean(containerEl.childElementCount) ||
      Boolean(containerEl.childNodes?.length);

    if (hasMountedContent()) return resolve(true);

    let done = false;
    const finish = (value) => {
      if (done) return;
      done = true;
      resolve(value);
    };

    const timeoutId = window.setTimeout(() => finish(false), timeoutMs);

    const observer = new MutationObserver(() => {
      if (hasMountedContent()) {
        window.clearTimeout(timeoutId);
        observer.disconnect();
        finish(true);
      }
    });

    observer.observe(containerEl, { childList: true, subtree: true });
  });
}

export default function TurnstileWidget({
  siteKey: siteKeyProp,
  onToken,
  onError,
  onBeforeInteractive,
  onAfterInteractive,
  onInteractive,
  action,
  cData,
  theme = "light",
  size = "normal",
  scale,
  appearance = "always",
  execution = "render",
  retry = "auto",
  retryInterval = TURNSTILE_RETRY_INTERVAL_MS,
  refreshExpired = "auto",
  resetKey,
  className,
  style,
  showInlineError = true,
}) {
  const siteKey = useMemo(() => getSiteKey(siteKeyProp), [siteKeyProp]);
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  const onBeforeInteractiveRef = useRef(onBeforeInteractive);
  const onAfterInteractiveRef = useRef(onAfterInteractive);
  const onInteractiveRef = useRef(onInteractive);
  const [renderError, setRenderError] = useState("");
  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onBeforeInteractiveRef.current = onBeforeInteractive;
  }, [onBeforeInteractive]);

  useEffect(() => {
    onAfterInteractiveRef.current = onAfterInteractive;
  }, [onAfterInteractive]);

  useEffect(() => {
    onInteractiveRef.current = onInteractive;
  }, [onInteractive]);

  const { widgetWidth, widgetHeight } = useMemo(() => {
    if (size === "compact") return { widgetWidth: 130, widgetHeight: 120 };
    if (size === "invisible") return { widgetWidth: 0, widgetHeight: 0 };
    return { widgetWidth: 300, widgetHeight: 65 };
  }, [size]);

  useEffect(() => {
    if (!siteKey) return undefined;
    if (!containerRef.current) return undefined;

    let canceled = false;

    async function render() {
      setRenderError("");
      setIsRendered(false);
      try {
        const api = await waitForTurnstile();
        if (canceled) return;

        // Ensure a clean slate if we're re-rendering in the same DOM node.
        if (widgetIdRef.current) {
          try {
            api.remove(widgetIdRef.current);
          } catch (_) {
            // ignore
          }
          widgetIdRef.current = null;
        }
        containerRef.current.innerHTML = "";

        widgetIdRef.current = api.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          size,
          appearance,
          execution,
          retry,
          "retry-interval": retryInterval,
          "refresh-expired": refreshExpired,
          action,
          cData,
          callback: (token) => {
            if (typeof onTokenRef.current === "function") {
              onTokenRef.current(token);
            }
          },
          "expired-callback": () => {
            if (typeof onTokenRef.current === "function") {
              onTokenRef.current("");
            }
          },
          "before-interactive-callback": () => {
            if (typeof onBeforeInteractiveRef.current === "function") {
              onBeforeInteractiveRef.current();
            }
            if (typeof onInteractiveRef.current === "function") {
              onInteractiveRef.current();
            }
          },
          "after-interactive-callback": () => {
            if (typeof onAfterInteractiveRef.current === "function") {
              onAfterInteractiveRef.current();
            }
          },
          "error-callback": () => {
            const err =
              "Verification failed. Please refresh the page and try again.";
            setRenderError(err);
            setIsRendered(false);
            if (typeof onErrorRef.current === "function") {
              onErrorRef.current(new Error(err));
            }
          },
        });

        if (execution === "execute" && typeof api.execute === "function") {
          api.execute(widgetIdRef.current);
        }

        // Turnstile's render() can return before its iframe mounts, which causes
        // a visible "blank" gap if we hide the skeleton too early.
        if (size === "invisible") {
          if (!canceled) setIsRendered(true);
          return;
        }

        // Turnstile may mount DOM content (e.g. a "verifying" state) before it inserts an iframe.
        // We consider the widget "ready to show" as soon as it has mounted any content.
        await waitForTurnstileMount(containerRef.current);
        if (!canceled) setIsRendered(true);
      } catch (e) {
        if (canceled) return;
        const msg = e?.message || "Unable to load Turnstile.";
        setRenderError(msg);
        setIsRendered(false);
        if (typeof onErrorRef.current === "function") {
          onErrorRef.current(e);
        }
      }
    }

    render();

    return () => {
      canceled = true;
      const api = typeof window !== "undefined" ? window.turnstile : null;
      if (api?.remove && widgetIdRef.current) {
        try {
          api.remove(widgetIdRef.current);
        } catch (_) {
          // ignore
        }
      }
      widgetIdRef.current = null;
    };
  }, [
    siteKey,
    theme,
    size,
    appearance,
    execution,
    retry,
    retryInterval,
    refreshExpired,
    action,
    cData,
  ]);

  useEffect(() => {
    if (!resetKey) return;
    const api = typeof window !== "undefined" ? window.turnstile : null;
    if (!api?.reset || !widgetIdRef.current) return;
    try {
      api.reset(widgetIdRef.current);
    } catch (_) {
      // ignore
    }
  }, [resetKey]);

  if (!siteKey) return null;

  const wrapperStyle = {
    ...style,
    "--turnstile-width": `${widgetWidth}px`,
    "--turnstile-height": `${widgetHeight}px`,
    ...(typeof scale === "number" && Number.isFinite(scale)
      ? { "--turnstile-scale-default": scale }
      : null),
  };

  return (
    <div
      className={["turnstile-widget", className].filter(Boolean).join(" ")}
      style={wrapperStyle}
    >
      <div className="turnstile-widget__scaled">
        {!isRendered && !renderError ? (
          <div className="turnstile-widget__skeleton" aria-hidden="true" />
        ) : null}
        <div
          ref={containerRef}
          className="turnstile-widget__container"
          style={{ opacity: isRendered ? 1 : 0 }}
        />
      </div>
      {showInlineError && renderError ? (
        <div style={{ marginTop: 8, fontSize: 13, color: "#b91c1c" }}>
          {renderError}
        </div>
      ) : null}
    </div>
  );
}
